import React, { useState, useEffect } from 'react';
import axios from 'axios';

const GooglePlacesLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLogIndex, setExpandedLogIndex] = useState(null);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [apiTypeFilter, setApiTypeFilter] = useState('');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [limit, apiTypeFilter]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: limit.toString() });
      if (apiTypeFilter) {
        params.append('api_type', apiTypeFilter);
      }
      const response = await axios.get(`/google-places-logs?${params.toString()}`);
      if (response.data.success) {
        setLogs(response.data.logs);
        setTotal(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching Google Maps API logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await axios.get('/google-places-logs/stats');
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching Google Maps API stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all Google Maps API logs? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await axios.delete('/google-places-logs');
      if (response.data.success) {
        alert(`Successfully cleared ${response.data.deleted_count} logs`);
        fetchLogs();
        fetchStats();
      }
    } catch (error) {
      console.error('Error clearing logs:', error);
      alert('Error clearing logs. Please try again.');
    }
  };

  const toggleLogExpansion = (index) => {
    setExpandedLogIndex(expandedLogIndex === index ? null : index);
  };

  const getStatusColor = (status) => {
    if (status === 'OK') {
      return 'bg-green-100 text-green-800';
    } else if (status === 'ZERO_RESULTS') {
      return 'bg-yellow-100 text-yellow-800';
    } else if (status === 'NOT_FOUND' || status === 'INVALID_REQUEST') {
      return 'bg-orange-100 text-orange-800';
    } else if (status === 'REQUEST_DENIED' || status === 'OVER_QUERY_LIMIT' || status === 'EXCEPTION') {
      return 'bg-red-100 text-red-800';
    } else {
      return 'bg-gray-100 text-gray-800';
    }
  };

  const getApiTypeLabel = (apiType) => {
    switch (apiType) {
      case 'google_distance_matrix':
        return { label: 'Distance Matrix', color: 'bg-purple-100 text-purple-800', icon: '🚗' };
      case 'google_places':
        return { label: 'Place Details', color: 'bg-blue-100 text-blue-800', icon: '📍' };
      default:
        return { label: apiType || 'Unknown', color: 'bg-gray-100 text-gray-800', icon: '❓' };
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);
    return (
      <button
        onClick={() => {
          navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-50"
        title="Copy to clipboard"
      >
        {copied ? '✓ Copied' : '📋 Copy'}
      </button>
    );
  };

  const renderPayload = (payload, title) => {
    if (!payload) return null;
    const jsonString = JSON.stringify(payload, null, 2);
    return (
      <div className="mt-2">
        <div className="flex items-center justify-between mb-1">
          <h5 className="font-semibold text-sm text-gray-700">{title}:</h5>
          <CopyButton text={jsonString} />
        </div>
        <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto border border-gray-200 max-h-96">
          {jsonString}
        </pre>
      </div>
    );
  };

  const renderDistanceMatrixLog = (log, index) => {
    const request = log.request || {};
    const response = log.response || {};

    return (
      <div key={index} className="p-4 hover:bg-gray-50">
        <div
          className="cursor-pointer"
          onClick={() => toggleLogExpansion(index)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
                  🚗 Distance Matrix
                </span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(log.status)}`}>
                  {log.status || 'N/A'}
                </span>
                {log.error && (
                  <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                    ERROR
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-900 mb-1">
                <span className="font-medium">From:</span>{' '}
                <span className="text-gray-700">{request.origin_address || request.origin || 'N/A'}</span>
              </div>
              <div className="text-sm text-gray-900 mb-1">
                <span className="font-medium">To:</span>{' '}
                <span className="text-gray-700">{request.destination_address || request.destination || 'N/A'}</span>
              </div>
              {response.duration_text && (
                <div className="text-sm text-green-700 font-medium mb-1">
                  ⏱️ {response.duration_text} ({response.distance_text || 'N/A'})
                </div>
              )}
              <div className="text-xs text-gray-500">
                {formatTimestamp(log.timestamp)}
              </div>
            </div>
            <div className="ml-4 flex-shrink-0">
              <svg
                className={`h-5 w-5 text-gray-400 transform transition-transform ${
                  expandedLogIndex === index ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {expandedLogIndex === index && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            {log.error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                <h5 className="font-semibold text-sm text-red-800 mb-1">Error:</h5>
                <p className="text-sm text-red-700">{log.error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <h5 className="font-semibold text-sm text-gray-700 mb-2">Request</h5>
                <div className="bg-gray-50 p-3 rounded border border-gray-200 space-y-2">
                  <div className="text-sm">
                    <span className="font-medium">Origin:</span>{' '}
                    <span className="text-gray-700">{request.origin || 'N/A'}</span>
                  </div>
                  {request.origin_place_id && (
                    <div className="text-sm">
                      <span className="font-medium">Origin Place ID:</span>{' '}
                      <span className="font-mono text-xs text-blue-600">{request.origin_place_id}</span>
                    </div>
                  )}
                  <div className="text-sm">
                    <span className="font-medium">Destination:</span>{' '}
                    <span className="text-gray-700">{request.destination || 'N/A'}</span>
                  </div>
                  {request.destination_place_id && (
                    <div className="text-sm">
                      <span className="font-medium">Dest Place ID:</span>{' '}
                      <span className="font-mono text-xs text-blue-600">{request.destination_place_id}</span>
                    </div>
                  )}
                  <div className="text-sm">
                    <span className="font-medium">Mode:</span>{' '}
                    <span className="text-gray-700">{request.mode || 'driving'}</span>
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-semibold text-sm text-gray-700 mb-2">Response</h5>
                {response.duration_seconds !== undefined ? (
                  <div className="bg-gray-50 p-3 rounded border border-gray-200 space-y-2">
                    <div className="text-sm">
                      <span className="font-medium">Duration:</span>{' '}
                      <span className="text-green-700 font-medium">{response.duration_text}</span>
                      <span className="text-gray-500 ml-1">({response.duration_seconds}s)</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Distance:</span>{' '}
                      <span className="text-gray-700">{response.distance_text}</span>
                      <span className="text-gray-500 ml-1">({response.distance_meters}m)</span>
                    </div>
                    {response.origin_address_resolved && (
                      <div className="text-sm">
                        <span className="font-medium">Resolved Origin:</span>{' '}
                        <span className="text-gray-600 text-xs">{response.origin_address_resolved}</span>
                      </div>
                    )}
                    {response.destination_address_resolved && (
                      <div className="text-sm">
                        <span className="font-medium">Resolved Dest:</span>{' '}
                        <span className="text-gray-600 text-xs">{response.destination_address_resolved}</span>
                      </div>
                    )}
                    {response.used_traffic_data !== undefined && (
                      <div className="text-sm">
                        <span className="font-medium">Traffic Data:</span>{' '}
                        <span className={response.used_traffic_data ? 'text-green-600' : 'text-gray-500'}>
                          {response.used_traffic_data ? 'Yes' : 'No'}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-50 p-3 rounded border border-gray-200 text-sm text-gray-500">
                    No response data
                  </div>
                )}
              </div>
            </div>

            {/* Full JSON */}
            {renderPayload(log, 'Full Log Entry')}
          </div>
        )}
      </div>
    );
  };

  const renderPlacesLog = (log, index) => {
    return (
      <div key={index} className="p-4 hover:bg-gray-50">
        <div
          className="cursor-pointer"
          onClick={() => toggleLogExpansion(index)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  📍 Place Details
                </span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(log.status)}`}>
                  {log.status || 'N/A'}
                </span>
                {log.error && (
                  <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                    ERROR
                  </span>
                )}
              </div>
              <div className="text-sm font-mono text-gray-900 break-all mb-1">
                Place ID: <span className="text-blue-600">{log.place_id}</span>
              </div>
              {log.response_data?.formatted_address && (
                <div className="text-sm text-gray-700 mb-1">
                  Address: {log.response_data.formatted_address}
                </div>
              )}
              <div className="text-xs text-gray-500">
                {formatTimestamp(log.timestamp)}
              </div>
            </div>
            <div className="ml-4 flex-shrink-0">
              <svg
                className={`h-5 w-5 text-gray-400 transform transition-transform ${
                  expandedLogIndex === index ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {expandedLogIndex === index && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            {log.error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                <h5 className="font-semibold text-sm text-red-800 mb-1">Error:</h5>
                <p className="text-sm text-red-700">{log.error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <h5 className="font-semibold text-sm text-gray-700 mb-2">Request</h5>
                <div className="bg-gray-50 p-3 rounded border border-gray-200">
                  <div className="text-sm mb-2">
                    <span className="font-medium">Place ID:</span>{' '}
                    <span className="font-mono text-blue-600">{log.place_id}</span>
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">Fields:</span>{' '}
                    <span className="font-mono">
                      {log.request_fields?.join(', ') || 'formatted_address, name, place_id'}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-semibold text-sm text-gray-700 mb-2">Response</h5>
                {log.response_data ? (
                  <div className="bg-gray-50 p-3 rounded border border-gray-200">
                    {log.response_data.name && (
                      <div className="text-sm mb-1">
                        <span className="font-medium">Name:</span> {log.response_data.name}
                      </div>
                    )}
                    {log.response_data.formatted_address && (
                      <div className="text-sm mb-1">
                        <span className="font-medium">Address:</span> {log.response_data.formatted_address}
                      </div>
                    )}
                    {log.response_data.place_id && (
                      <div className="text-sm">
                        <span className="font-medium">Place ID:</span>{' '}
                        <span className="font-mono text-xs">{log.response_data.place_id}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-50 p-3 rounded border border-gray-200 text-sm text-gray-500">
                    No response data
                  </div>
                )}
              </div>
            </div>

            {/* Full Response JSON */}
            {renderPayload(log.response_data, 'Full Response JSON')}
          </div>
        )}
      </div>
    );
  };

  const renderLogEntry = (log, index) => {
    if (log.api_type === 'google_distance_matrix') {
      return renderDistanceMatrixLog(log, index);
    } else {
      return renderPlacesLog(log, index);
    }
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Google Maps API Logs</h1>
          <p className="mt-1 text-sm text-gray-500">
            View Google Maps API requests for Place Details and Distance Matrix (travel time) lookups
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            value={apiTypeFilter}
            onChange={(e) => setApiTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All API Types</option>
            <option value="google_places">📍 Place Details</option>
            <option value="google_distance_matrix">🚗 Distance Matrix</option>
          </select>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={25}>Show 25</option>
            <option value={50}>Show 50</option>
            <option value={100}>Show 100</option>
            <option value={200}>Show 200</option>
          </select>
          <button
            onClick={() => { fetchLogs(); fetchStats(); }}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Refresh
          </button>
          <button
            onClick={handleClearLogs}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && !statsLoading && (
        <div className="bg-white shadow rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">API Usage Statistics</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-gray-900">{stats.total_requests}</div>
              <div className="text-sm text-gray-500">Total Requests</div>
            </div>
            {stats.by_api_type?.google_places && (
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-blue-900">
                  {stats.by_api_type.google_places.total}
                  <span className="text-sm font-normal text-blue-600 ml-2">
                    ({stats.by_api_type.google_places.success} ok / {stats.by_api_type.google_places.errors} err)
                  </span>
                </div>
                <div className="text-sm text-blue-700">📍 Place Details</div>
              </div>
            )}
            {stats.by_api_type?.google_distance_matrix && (
              <div className="bg-purple-50 rounded-lg p-3">
                <div className="text-2xl font-bold text-purple-900">
                  {stats.by_api_type.google_distance_matrix.total}
                  <span className="text-sm font-normal text-purple-600 ml-2">
                    ({stats.by_api_type.google_distance_matrix.success} ok / {stats.by_api_type.google_distance_matrix.errors} err)
                  </span>
                </div>
                <div className="text-sm text-purple-700">🚗 Distance Matrix</div>
              </div>
            )}
          </div>
          {stats.last_24_hours && Object.keys(stats.last_24_hours).length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="text-xs text-gray-500">
                Last 24 hours:{' '}
                {Object.entries(stats.last_24_hours).map(([type, count], idx) => (
                  <span key={type}>
                    {idx > 0 && ', '}
                    {type === 'google_distance_matrix' ? '🚗' : '📍'} {count}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Log Count */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="text-sm text-gray-600">
          Showing <span className="font-semibold">{logs.length}</span> of{' '}
          <span className="font-semibold">{total}</span> total logs
          {apiTypeFilter && (
            <span className="ml-2 text-gray-400">
              (filtered by {apiTypeFilter === 'google_distance_matrix' ? '🚗 Distance Matrix' : '📍 Place Details'})
            </span>
          )}
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg">No Google Maps API logs yet</p>
            <p className="text-sm mt-2">
              {apiTypeFilter === 'google_distance_matrix'
                ? 'Distance Matrix API requests will appear here when calculating travel times for chauffeur bookings'
                : apiTypeFilter === 'google_places'
                ? 'Place Details API requests will appear here when looking up location details'
                : 'API requests will appear here when using location and travel time features'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {logs.map((log, index) => renderLogEntry(log, index))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GooglePlacesLogs;
